import { defineStore } from 'pinia';
import http from '../api/httpRequest.js'
import { TERMINAL_TYPE } from "../api/enums.js"
import { getDB } from '../db/index.js';
import { attachContactSearchText } from '../utils/contactSearchMatcher.js';
import { applySyncPageState, createPageState, createSyncState, resetPageState } from './listPageState.js';
const FRIEND_LIST_FIRST_PAGE_LIMIT = 30;
const FRIEND_LIST_PAGE_LIMIT = 30;
const FRIEND_LIST_BULK_PAGE_LIMIT = 500;
const FRIEND_LIST_PAGE_LIMIT_MAX = 500;
const FRIEND_LIST_BACKGROUND_PAGE_BUDGET = 4;
const FRIEND_LIST_FULL_LOAD_PAGE_BUDGET = 500;

function resolveIdKey(id) {
	if (id === undefined || id === null || id === '') {
		return '';
	}
	return String(id);
}

function toNumber(value, fallback = 0) {
	const num = Number(value);
	return Number.isFinite(num) ? num : fallback;
}

function toIntVersion(value) {
	const normalized = Math.floor(toNumber(value, 0));
	return normalized > 0 ? normalized : 0;
}

function normalizeFriendListPagePayload(payload) {
	const normalized = payload && typeof payload === 'object' ? payload : {};
	const items = Array.isArray(normalized.items) ? normalized.items : [];
	const total = Math.floor(toNumber(normalized.total, 0));
	return {
		items,
		more: Boolean(normalized.more),
		total: total > 0 ? total : 0,
		cursor: normalized.cursor ? String(normalized.cursor) : '',
		rev: toIntVersion(normalized.rev),
		reset: normalized.reset === true,
		reason: normalized.reason ? String(normalized.reason) : ''
	};
}

function prepareFriendRuntime(friend) {
	if (!friend || typeof friend !== 'object') {
		return friend;
	}
	attachContactSearchText(friend);
	return friend;
}

function mergeFriendPatch(targetFriend, incomingFriend = {}) {
	if (!targetFriend || !incomingFriend || typeof incomingFriend !== 'object') {
		return;
	}
	const patch = { ...incomingFriend };
	// 分组成员接口返回的 friend 可能带空 groupId，不能覆盖好友主快照中的分组归属。
	if (patch.groupId === undefined || patch.groupId === null || patch.groupId === '') {
		delete patch.groupId;
	}
	Object.assign(targetFriend, patch);
}

export default defineStore('friendStore', {
	state: () => {
		return {
			friends: [], // 数组用于展示，防止更新时不刷新
			friendMap: new Map(), // map用于查询，提高查询效率
			requests: [], // 好友申请列表
			page: createPageState(),
			sync: createSyncState(),
			tasks: {
				bg: null,
				load: null,
				full: null,
				localReady: null
			}
		}
	},
	actions: {
		resetFriendListState(options = {}) {
			const clearTasks = options.clearTasks !== false;
			this.sync.rev = 0;
			this.sync.base = 0;
			resetPageState(this.page);
			if (clearTasks) {
				this.tasks.bg = null;
				this.tasks.load = null;
				this.tasks.full = null;
			}
		},
		setFriendListPageState(page, baseRev) {
			applySyncPageState(this.page, this.sync, page, baseRev, toIntVersion);
		},
		activeFriendCount() {
			return this.friends.filter((friend) => friend && !friend.deleted).length;
		},
		hasCompleteFriendSnapshot() {
			const total = Math.max(0, Math.floor(Number(this.page.total || 0)));
			return total > 0 && !this.page.more && this.activeFriendCount() >= total;
		},
		hasIncompleteFriendSnapshot() {
			const total = Math.max(0, Math.floor(Number(this.page.total || 0)));
			return total > 0 && !this.page.more && this.activeFriendCount() < total;
		},
		init(friends) {
			this.friends = Array.isArray(friends) ? friends : [];
			this.friendMap.clear();
			this.friends.forEach(f => {
				prepareFriendRuntime(f);
				const friendKey = resolveIdKey(f && f.id);
				if (friendKey) {
					this.friendMap.set(friendKey, f);
				}
			})
		},
			append(friends) {
				(Array.isArray(friends) ? friends : []).forEach(f => {
					const friendKey = resolveIdKey(f && f.id);
					if (!friendKey) {
						return;
					}
					prepareFriendRuntime(f);
					if (this.friendMap.has(friendKey)) {
						const friend = this.friendMap.get(friendKey);
						mergeFriendPatch(friend, f);
						prepareFriendRuntime(friend);
					} else {
						this.friends.push(f);
						this.friendMap.set(friendKey, f);
					}
			})
		},
		resetOnline(friends) {
			(Array.isArray(friends) ? friends : []).forEach(f => {
				f.online = false;
				f.onlineWeb = false;
				f.onlineApp = false;
			})
		},
		setRequests(requests) {
			this.requests = requests;
		},
		addRequest(request) {
			this.requests.unshift(request);
		},
		removeRequest(id) {
			for (let idx in this.requests) {
				if (this.requests[idx].id == id) {
					this.requests.splice(idx, 1);
					break;
				}
			}
		},
		async updateFriend(friend) {
			const f = this.findFriend(friend.id);
			if (!f) {
				return;
			}
			friend.online = f.online;
			friend.onlineWeb = f.onlineWeb;
			friend.onlineApp = f.onlineApp;
			prepareFriendRuntime(friend);
			Object.assign(f, friend);
			prepareFriendRuntime(f);
			await getDB().saveFriend(friend);
		},
		async patchFriendProfile(user) {
			if (!user || user.id === undefined || user.id === null) {
				return null;
			}
			const friend = this.findFriend(user.id);
			if (!friend || friend.deleted) {
				return null;
			}
			const nextHeadImage = user.headImageThumb || user.headImage || '';
			let changed = false;
			if (friend.nickName !== user.nickName) {
				friend.nickName = user.nickName || '';
				changed = true;
			}
			if (friend.headImage !== nextHeadImage) {
				friend.headImage = nextHeadImage;
				changed = true;
			}
			if (user.userName && friend.userName !== user.userName) {
				friend.userName = user.userName;
				changed = true;
			}
			const nextShowNickName = friend.remarkNickName || friend.nickName || friend.userName || '';
			if (friend.showNickName !== nextShowNickName) {
				friend.showNickName = nextShowNickName;
				changed = true;
			}
			prepareFriendRuntime(friend);
			if (changed) {
				await getDB().saveFriend(friend);
			}
			return friend;
		},
		async removeFriend(id) {
			const friend = this.findFriend(id);
			if (!friend) {
				return;
			}
			friend.deleted = true;
			await getDB().saveFriend(friend);
		},
		async addFriend(friend) {
			const friendKey = resolveIdKey(friend && friend.id);
			if (!friendKey) {
				return;
			}
			prepareFriendRuntime(friend);
			if (this.friendMap.has(friendKey)) {
				this.updateFriend(friend)
			} else {
				this.friends.unshift(friend);
				this.friendMap.set(friendKey, friend);
				await getDB().saveFriend(friend);
			}
		},
		updateOnlineStatus(onlineData) {
			const friend = this.findFriend(onlineData.userId);
			if (!friend) {
				return;
			}
			if (onlineData.terminal == TERMINAL_TYPE.WEB) {
				friend.onlineWeb = onlineData.online;
			} else if (onlineData.terminal == TERMINAL_TYPE.APP) {
				friend.onlineApp = onlineData.online;
			}
			friend.online = friend.onlineWeb || friend.onlineApp;
		},
		clear() {
			this.friends = [];
			this.friendMap.clear();
			this.requests = [];
			this.resetFriendListState();
		},
		fetchFriendListPage({ rev = 0, cursor = '', limit = FRIEND_LIST_PAGE_LIMIT, includeOnline = false } = {}) {
			const requestRev = toIntVersion(rev);
			const safeLimit = Math.max(1, Math.min(FRIEND_LIST_PAGE_LIMIT_MAX, toNumber(limit, FRIEND_LIST_PAGE_LIMIT) || FRIEND_LIST_PAGE_LIMIT));
			const params = new URLSearchParams();
			params.set('rev', String(requestRev));
			params.set('limit', String(safeLimit));
			params.set('includeOnline', includeOnline ? 'true' : 'false');
			if (cursor) {
				params.set('cursor', String(cursor));
			}
			return http({
				url: `/friend/list?${params.toString()}`,
				method: 'GET'
			}).then(payload => normalizeFriendListPagePayload(payload));
		},
		async pullFriends(options = {}) {
			const requestRev = toIntVersion(options.rev !== undefined ? options.rev : this.sync.rev);
			const page = await this.fetchFriendListPage({
				rev: requestRev,
				cursor: options.cursor || '',
				limit: options.limit || FRIEND_LIST_PAGE_LIMIT,
				includeOnline: false
			});
			if (page.reset === true && requestRev > 0) {
				await getDB().syncAllFriends([]);
				this.init([]);
				this.resetFriendListState();
				return this.pullFriends({
					...options,
					rev: 0,
					cursor: ''
				});
			}
			const items = Array.isArray(page.items) ? page.items : [];
			this.resetOnline(items);
			if (requestRev > 0) {
				this.append(items);
			} else {
				this.init(items);
			}
			await getDB().saveFriends(items);
			this.setFriendListPageState(page, requestRev);
			return page;
		},
		async loadMoreFriendsFromCursor(options = {}) {
			if (this.tasks.bg) {
				return this.tasks.bg;
			}
			if (this.page.loading || !this.page.more || !this.page.cursor) {
				return Promise.resolve({
					items: [],
					more: this.page.more
				});
			}
			const task = (async () => {
				const requestRev = toIntVersion(options.rev !== undefined ? options.rev : this.sync.base);
				const pageBudget = Math.max(1, Math.floor(toNumber(options.maxPages, FRIEND_LIST_BACKGROUND_PAGE_BUDGET) || FRIEND_LIST_BACKGROUND_PAGE_BUDGET));
				const pageLimit = Math.max(1, Math.min(FRIEND_LIST_PAGE_LIMIT_MAX, toNumber(options.limit, FRIEND_LIST_PAGE_LIMIT) || FRIEND_LIST_PAGE_LIMIT));
				let cursor = this.page.cursor;
				let more = this.page.more;
				let fetchedPages = 0;
				let mergedItems = [];
				this.page.loading = true;
				this.page.error = false;
				try {
					while (more && cursor && fetchedPages < pageBudget) {
						const page = await this.fetchFriendListPage({
							rev: requestRev,
							cursor,
							limit: pageLimit,
							includeOnline: false
						});
						if (page.reset === true && requestRev > 0) {
							await getDB().syncAllFriends([]);
							this.init([]);
							this.resetFriendListState();
							this.page.loading = false;
							await this.pullFriends({
								rev: 0,
								cursor: '',
								limit: FRIEND_LIST_FIRST_PAGE_LIMIT
							});
							break;
						}
						const items = Array.isArray(page.items) ? page.items : [];
						this.resetOnline(items);
						this.append(items);
						await getDB().saveFriends(items);
						mergedItems = mergedItems.concat(items);
						this.setFriendListPageState(page, requestRev);
						cursor = this.page.cursor;
						more = this.page.more;
						fetchedPages += 1;
					}
					return {
						items: mergedItems,
						more: this.page.more
					};
					} catch (error) {
						this.page.error = true;
						throw error;
					} finally {
					this.page.loading = false;
				}
			})();
			const trackedTask = task.finally(() => {
				this.tasks.bg = null;
			});
			this.tasks.bg = trackedTask;
			return trackedTask;
		},
			async ensureFriendListFullyLoaded(options = {}) {
				if (this.tasks.full) {
					return this.tasks.full;
				}
				const task = (async () => {
					if (this.tasks.load) {
						await this.tasks.load;
					}
					const pageLimit = Math.max(1, Math.min(
						FRIEND_LIST_PAGE_LIMIT_MAX,
						Math.max(
							toNumber(options.limit, FRIEND_LIST_BULK_PAGE_LIMIT) || FRIEND_LIST_BULK_PAGE_LIMIT,
							FRIEND_LIST_BULK_PAGE_LIMIT
						)
					));
					const maxPages = Math.max(1, Math.floor(
						toNumber(options.maxPages, FRIEND_LIST_FULL_LOAD_PAGE_BUDGET) || FRIEND_LIST_FULL_LOAD_PAGE_BUDGET
					));
					if (this.hasCompleteFriendSnapshot()) {
						return this.friends;
					}
					if (this.sync.rev > 0 && this.page.total <= 0 && !this.page.more) {
						await this.pullFriends({
							rev: this.sync.rev,
							cursor: '',
							limit: pageLimit
						});
					}
					if (this.hasCompleteFriendSnapshot()) {
						return this.friends;
					}
					const total = Math.max(0, Math.floor(Number(this.page.total || 0)));
					if (this.sync.rev > 0 && total > 0 && !this.page.more && this.activeFriendCount() < total) {
						// 本地 IndexedDB 快照缺页时才重建全量快照，避免正常全选重复拉取全部好友。
						await this.pullFriends({
							rev: 0,
							cursor: '',
							limit: pageLimit
						});
					}
					let loadedPages = 0;
					while (this.page.more && this.page.cursor && loadedPages < maxPages) {
						await this.loadMoreFriendsFromCursor({
							...options,
							limit: pageLimit,
							maxPages: 1
						});
						loadedPages += 1;
					}
					return this.friends;
				})();
				const trackedTask = task.finally(() => {
					if (this.tasks.full === trackedTask) {
						this.tasks.full = null;
				}
			});
			this.tasks.full = trackedTask;
			return trackedTask;
		},
		async refreshOnline() {
			const onlines = await http({ url: '/friend/online' });
			this.resetOnline(this.friends);
			onlines.forEach(online => this.updateOnlineStatus(online));
		},
		async loadRequest() {
			const requests = await http({ url: '/friend/request/list' });
			this.setRequests(requests);
		},
		searchUsers(keyword) {
			return http({
				url: '/user/search',
				method: 'GET',
				params: {
					name: String(keyword || '').trim()
				}
			});
		},
		applyFriendRequest(payload = {}) {
			return http({
				url: '/friend/request/apply',
				method: 'POST',
				data: payload
			});
		},
		approveFriendRequest(requestId) {
			return http({
				url: `/friend/request/approve?id=${requestId}`,
				method: 'POST'
			});
		},
		rejectFriendRequest(requestId) {
			return http({
				url: `/friend/request/reject?id=${requestId}`,
				method: 'POST'
			});
		},
		recallFriendRequest(requestId) {
			return http({
				url: `/friend/request/recall?id=${requestId}`,
				method: 'POST'
			});
		},
		async loadFriend(options = {}) {
			if (this.tasks.load) {
				return this.tasks.load;
			}
			let resolveLocalReady = null;
			let rejectLocalReady = null;
			const localReadyPromise = new Promise((resolve, reject) => {
				resolveLocalReady = resolve;
				rejectLocalReady = reject;
			});
			this.tasks.localReady = localReadyPromise;
			const task = (async () => {
				try {
					const localFriends = await getDB().findAllFriends();
					this.resetOnline(localFriends);
					this.init(localFriends);
					this.resetFriendListState({
						clearTasks: false
					});
					this.sync.rev = this.maxFriendVersion();
					this.sync.base = this.sync.rev;
					resolveLocalReady && resolveLocalReady(localFriends);
				} catch (error) {
					rejectLocalReady && rejectLocalReady(error);
					throw error;
				}
				let requestRev = toIntVersion(options.rev !== undefined ? options.rev : this.sync.rev);
				const firstPageLimit = Math.max(1, Math.min(FRIEND_LIST_PAGE_LIMIT_MAX,
					toNumber(options.firstPageLimit, FRIEND_LIST_FIRST_PAGE_LIMIT) || FRIEND_LIST_FIRST_PAGE_LIMIT));
				this.page.loading = true;
				this.page.error = false;
				let firstPage;
				try {
					firstPage = await this.pullFriends({
						rev: requestRev,
						cursor: '',
						limit: firstPageLimit
					});
					if (requestRev > 0 && this.hasIncompleteFriendSnapshot()) {
						firstPage = await this.pullFriends({
							rev: 0,
							cursor: '',
							limit: firstPageLimit
						});
					}
					requestRev = this.sync.base;
				} catch (error) {
					this.page.error = true;
					this.page.loading = false;
					throw error;
				}
				this.page.loading = false;
				await this.loadRequest();
				this.refreshOnline().catch((e) => {
					console.warn("刷新好友在线状态失败", e);
				});
				return firstPage;
			})();
			const trackedTask = task.finally(() => {
				if (this.tasks.load === trackedTask) {
					this.tasks.load = null;
				}
				if (this.tasks.localReady === localReadyPromise) {
					this.tasks.localReady = null;
				}
			});
			this.tasks.load = trackedTask;
			return trackedTask;
		},
		loadFriendLocalReady() {
			return this.tasks.localReady || Promise.resolve();
		}
	},
	getters: {
		maxFriendVersion: (state) => () => {
			return Math.max(0, ...state.friends.map(f => Number(f.version) || 0));
		},
		isFriend: (state) => (userId) => {
			let f = state.findFriend(userId)
			return f && !f.deleted
		},
		findFriend: (state) => (userId) => {
			const friendKey = resolveIdKey(userId);
			return friendKey ? state.friendMap.get(friendKey) : null;
		},
		isInRecvRequest: (state) => (userId) => {
			return state.requests.some((req) => req.recvId == userId);
		},
		isInSendRequest: (state) => (userId) => {
			return state.requests.some((req) => req.sendId == userId);
		}
	}
});
